import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

import { fetchChapterDetails } from "@/api/fetchChapterDetails/fetchChapterDetailsApi";
import { ChapterDetails } from "@/api/fetchChapterDetails/types";

// Import your Card components
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import MyChapterDashboardLoadingSkeleton from "@/features/myChapter/components/MyChapterDashboardLoadingSkeleton/MyChapterDashboardLoadingSkeleton";
import EmailNotificationModal from "@/features/myEmailNotifications/components/EmailNotificationModal/EmailNotificationModal";

const MyChapterDashboard: React.FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [chapterDetails, setChapterDetails] = useState<ChapterDetails | null>(
    null,
  );
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);

  // Fetch chapter details on component mount
  useEffect(() => {
    const getChapterDetails = async () => {
      try {
        setIsLoading(true);
        const response = await fetchChapterDetails();
        setChapterDetails(response.data);
        setError(null);
      } catch (err) {
        console.error("Error fetching chapter details:", err);
        setError("Failed to load chapter details. Please try again later.");
      } finally {
        setIsLoading(false);
      }
    };

    getChapterDetails();
  }, []);

  // Data for leadership chart (advisors and officers)
  const leadershipData = chapterDetails
    ? [
        { name: "Advisors", value: chapterDetails.numOfCurrentAdvisors || 0 },
        { name: "Officers", value: chapterDetails.numOfCurrentOfficers || 0 },
      ]
    : [];

  // Separate color palette for the leadership chart
  const LEADERSHIP_COLORS = ["#8884d8", "#82ca9d"]; // Purple and green

  // Create membership trend data from dynamic year counts
  const membershipTrendData = chapterDetails
    ? [
        // Sort by year ascending (oldest to newest) and map to chart format
        ...[...chapterDetails.memberCountsByYear]
          .sort((a, b) => a.year - b.year)
          .map((item) => ({
            year: item.year.toString(),
            members: item.count,
          })),
        // Add the goal if available
        ...(chapterDetails.fyGoal
          ? [{ year: "Goal", members: chapterDetails.fyGoal }]
          : []),
      ]
    : [];

  // Handle loading and error states
  if (isLoading) {
    return <MyChapterDashboardLoadingSkeleton />;
  }

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center">
        <p className="text-red-500">{error}</p>
        <Button className="mt-4" onClick={() => window.location.reload()}>
          Retry
        </Button>
      </div>
    );
  }

  if (!chapterDetails) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        No chapter details available.
      </div>
    );
  }

  return (
    <MainPageWrapper
      actions={
        <Button
          onClick={() => setIsNotificationModalOpen(true)}
          variant="default"
        >
          Notify Members
        </Button>
      }
      title={chapterDetails.companyName || "Chapter Dashboard"}
    >
      <div>
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <p className="pr-20 text-gray-700">
              {chapterDetails.description ||
                "Chapter information and statistics"}
            </p>
          </div>
        </div>

        {/* Section 1: Stats Cards */}
        <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
          {/* Advisors */}
          <Card>
            <CardHeader>
              <CardTitle>Advisors</CardTitle>
              <CardDescription>Total Number of Advisors</CardDescription>
            </CardHeader>
            <CardContent>
              <span className="text-2xl font-bold text-gray-800">
                {chapterDetails.numOfCurrentAdvisors ?? 0}
              </span>
            </CardContent>
          </Card>

          {/* Officers */}
          <Card>
            <CardHeader>
              <CardTitle>Officers</CardTitle>
              <CardDescription>Total Number of Officers</CardDescription>
            </CardHeader>
            <CardContent>
              <span className="text-2xl font-bold text-gray-800">
                {chapterDetails.numOfCurrentOfficers ?? 0}
              </span>
            </CardContent>
          </Card>

          {/* Members */}
          <Card>
            <CardHeader>
              <CardTitle>Members</CardTitle>
              <CardDescription>Total Number of Members</CardDescription>
            </CardHeader>
            <CardContent>
              <span className="text-2xl font-bold text-gray-800">
                {chapterDetails.numOfMembers ?? 0}
              </span>
            </CardContent>
          </Card>
        </div>

        {/* Section 2: Charts */}
        <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2">
          {/* Leadership Pie Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Leadership Composition</CardTitle>
              <CardDescription>
                Breakdown of advisors and officers.
              </CardDescription>
            </CardHeader>
            <CardContent className="mt-2 flex h-[300px] w-full">
              <ResponsiveContainer height="100%" width="100%">
                <PieChart>
                  <Pie
                    data={leadershipData}
                    dataKey="value"
                    fill="#8884d8"
                    label
                    nameKey="name"
                    outerRadius="80%"
                  >
                    {leadershipData.map((entry, index) => (
                      <Cell
                        fill={
                          LEADERSHIP_COLORS[index % LEADERSHIP_COLORS.length]
                        }
                        key={`leadership-cell-${index}`}
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Bar Chart (Membership Growth) */}
          <Card>
            <CardHeader>
              <CardTitle>Membership Growth</CardTitle>
              <CardDescription>
                Yearly membership counts and goal.
              </CardDescription>
            </CardHeader>
            <CardContent className="mt-2 flex h-[300px] w-full">
              <ResponsiveContainer height="100%" width="100%">
                <BarChart data={membershipTrendData}>
                  <XAxis dataKey="year" />
                  <YAxis />
                  <Tooltip
                    formatter={(value, name) => [
                      value,
                      name === "members" ? "Members" : name,
                    ]}
                  />
                  <Legend />
                  <Bar
                    dataKey="members"
                    fill="#8884d8"
                    name="Members"
                    // Special color for the goal bar if present
                    isAnimationActive={true}
                  >
                    {membershipTrendData.map((entry, index) => (
                      <Cell
                        fill={entry.year === "Goal" ? "#82ca9d" : "#8884d8"}
                        key={`bar-cell-${index}`}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Section 3: Basic Chapter Info */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Chapter Info</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-sm text-gray-700">
              <span className="font-medium">Founded Year:</span>{" "}
              {chapterDetails.foundedYear || "N/A"}
            </p>
            <p className="text-sm text-gray-700">
              <span className="font-medium">Chapter Founded Date:</span>{" "}
              {chapterDetails.chapterFoundedDate || "N/A"}
            </p>
            <p className="text-sm text-gray-700">
              <span className="font-medium">Chapter Type:</span>{" "}
              {chapterDetails.chapterType || "N/A"}
            </p>
            <p className="text-sm text-gray-700">
              <span className="font-medium">School Abbreviation:</span>{" "}
              {chapterDetails.schoolAbbreviation || "N/A"}
            </p>
            <p className="text-sm text-gray-700">
              <span className="font-medium">Phone:</span>{" "}
              {chapterDetails.phone || "N/A"}
            </p>
            <p className="text-sm text-gray-700">
              <span className="font-medium">Description:</span>{" "}
              {chapterDetails.description || "N/A"}
            </p>

            {/* New field: chapterSocialMedia (often an Instagram link) */}
            <p className="text-sm text-gray-700">
              <span className="font-medium">Social:</span>{" "}
              {chapterDetails.chapterSocialMedia ? (
                <a
                  className="text-indigo-600 hover:text-indigo-500"
                  href={chapterDetails.chapterSocialMedia}
                  rel="noreferrer"
                  target="_blank"
                >
                  {chapterDetails.chapterSocialMedia}
                </a>
              ) : (
                "N/A"
              )}
            </p>
          </CardContent>
        </Card>

        {/* Advisors and Officers */}
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          {/* Advisors */}
          <Card>
            <CardHeader>
              <CardTitle>Advisors</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {chapterDetails.advisor1 && (
                <p className="text-sm text-gray-700">
                  <span className="font-medium">Advisor 1:</span>{" "}
                  {chapterDetails.advisor1}
                  {chapterDetails.advisor1Title &&
                    ` (${chapterDetails.advisor1Title})`}
                  {chapterDetails.advisor1Email && " - "}
                  {chapterDetails.advisor1Email && (
                    <span className="underline">
                      {chapterDetails.advisor1Email}
                    </span>
                  )}
                </p>
              )}
              {chapterDetails.advisor2 && (
                <p className="text-sm text-gray-700">
                  <span className="font-medium">Advisor 2:</span>{" "}
                  {chapterDetails.advisor2}
                  {chapterDetails.advisor2Title &&
                    ` (${chapterDetails.advisor2Title})`}
                  {chapterDetails.advisor2Email && " - "}
                  {chapterDetails.advisor2Email && (
                    <span className="underline">
                      {chapterDetails.advisor2Email}
                    </span>
                  )}
                </p>
              )}
              {!chapterDetails.advisor1 && !chapterDetails.advisor2 && (
                <p className="text-sm text-gray-700">
                  No advisor information available.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Officers */}
          <Card>
            <CardHeader>
              <CardTitle>Officers</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {chapterDetails.officer1 && (
                <p className="text-sm text-gray-700">
                  <span className="font-medium">Officer 1:</span>{" "}
                  {chapterDetails.officer1}
                  {chapterDetails.officer1Position &&
                    ` (${chapterDetails.officer1Position})`}
                  {chapterDetails.officer1Email && " - "}
                  {chapterDetails.officer1Email && (
                    <span className="underline">
                      {chapterDetails.officer1Email}
                    </span>
                  )}
                </p>
              )}
              {chapterDetails.officer2 && (
                <p className="text-sm text-gray-700">
                  <span className="font-medium">Officer 2:</span>{" "}
                  {chapterDetails.officer2}
                  {chapterDetails.officer2Position &&
                    ` (${chapterDetails.officer2Position})`}
                  {chapterDetails.officer2Email && " - "}
                  {chapterDetails.officer2Email && (
                    <span className="underline">
                      {chapterDetails.officer2Email}
                    </span>
                  )}
                </p>
              )}
              {chapterDetails.officer3 && (
                <p className="text-sm text-gray-700">
                  <span className="font-medium">Officer 3:</span>{" "}
                  {chapterDetails.officer3}
                  {chapterDetails.officer3Position &&
                    ` (${chapterDetails.officer3Position})`}
                  {chapterDetails.officer3Email && " - "}
                  {chapterDetails.officer3Email && (
                    <span className="underline">
                      {chapterDetails.officer3Email}
                    </span>
                  )}
                </p>
              )}
              {chapterDetails.officer4 && (
                <p className="text-sm text-gray-700">
                  <span className="font-medium">Officer 4:</span>{" "}
                  {chapterDetails.officer4}
                  {chapterDetails.officer4Position &&
                    ` (${chapterDetails.officer4Position})`}
                  {chapterDetails.officer4Email && " - "}
                  {chapterDetails.officer4Email && (
                    <span className="underline">
                      {chapterDetails.officer4Email}
                    </span>
                  )}
                </p>
              )}
              {!chapterDetails.officer1 &&
                !chapterDetails.officer2 &&
                !chapterDetails.officer3 &&
                !chapterDetails.officer4 && (
                  <p className="text-sm text-gray-700">
                    No officer information available.
                  </p>
                )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Email Notification Modal */}
      <EmailNotificationModal
        isOpen={isNotificationModalOpen}
        onClose={() => setIsNotificationModalOpen(false)}
      />
    </MainPageWrapper>
  );
};

export default MyChapterDashboard;
